import { FC, useCallback, useMemo } from 'react';
import { Button } from '@carbon/react';
import { Close, Launch } from '@carbon/react/icons';
import { useAtomValue } from 'jotai';
import { Drawer, DrawerContent, DrawerHeader } from 'components/drawer/drawer';
import moment from 'moment';
import { createResourceAtom } from 'lib/atoms';
import { formatResource } from 'utils/misc';
import type {
  AreaConfigResponse,
  DevelopmentPaceScenarioResponse,
} from 'pages/development-pace-scenarios/scenario-form/types';
import {
  TIMING_SECTIONS,
  timingKey,
} from 'pages/development-pace-scenarios/scenario-form/timing-sections';
import type { LtdStyleTimingField } from 'types/dps-timing';
import style from './dps-detail-panel.module.scss';

interface Props {
  dpsId: number | null;
  open: boolean;
  onClose: () => void;
  /**
   * Calculation being viewed, when the panel is opened from a saved evaluation.
   * Scopes the fetch so a 'current_month' scenario reports the month THAT run
   * resolved to instead of re-resolving against today (INS-10499).
   */
  evaluationCalculationId?: number | null;
}

const PACE_MODEL_LABELS: Record<string, string> = {
  years_to_full_development: 'Years to Full Dev',
  fixed_rate: 'Fixed Rate',
};

const PRIORITIZATION_LABELS: Record<string, string> = {
  legacy_ranking: 'Legacy Ranking',
};

const fmt = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined) return '\u2014';
  return String(v);
};

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '\u2014';
  return moment(v).format('MM/DD/YYYY');
};

interface TableRow {
  section: string;
  parameter: string;
  areas: string[];
}

const buildRows = (dps: DevelopmentPaceScenarioResponse): TableRow[] => {
  const areas = dps.areaConfigs ?? [];
  const repeat = (value: string): string[] => areas.map(() => value);

  // A 'current_month' scenario resolves fresh on every run, so the stored
  // referenceDate can lag the month actually scheduled from. Drive every derived
  // date off the effective value, falling back for older cached responses.
  const refDate = dps.effectiveReferenceDate ?? dps.referenceDate;
  const startDateLabel =
    dps.referenceDateMode === 'current_month'
      ? `${moment(refDate).format('MMM YYYY')} (Current Month)`
      : moment(refDate).format('MMM YYYY');

  const rows: TableRow[] = [
    // Configuration section (scenario-wide \u2014 same value in every area column)
    {
      section: 'Configuration',
      parameter: 'Start Date',
      areas: repeat(startDateLabel),
    },
    {
      section: '',
      parameter: 'Dev Pace Model',
      areas: repeat(PACE_MODEL_LABELS[dps.paceModel] ?? dps.paceModel),
    },
    {
      section: '',
      parameter: 'Slot Prioritization',
      areas: repeat(
        PRIORITIZATION_LABELS[dps.slotPrioritizationMethod] ??
          dps.slotPrioritizationMethod
      ),
    },
    {
      section: '',
      parameter: 'Type Curve Scenario',
      // The name when selected, "Not selected" for migrated rows — never blank.
      areas: repeat(dps.typeCurveScenarioName ?? 'Not selected'),
    },
    {
      section: '',
      parameter:
        dps.paceModel === 'fixed_rate' ? 'Wells per Year' : 'Years to Full Dev',
      areas: areas.map((ac) => fmt(ac.paceModelValue)),
    },
    // LTD, then each Create New Well status's own LTD-style timing
    ...TIMING_SECTIONS.flatMap(({ prefix, title }) => {
      /**
       * A response cached before the per-status columns falls back to the area's LTD value.
       * @param {AreaConfigResponse} ac - The area config.
       * @param {LtdStyleTimingField} field - One of the section's fields.
       * @return {number | undefined} The months.
       */
      const value = (ac: AreaConfigResponse, field: LtdStyleTimingField) =>
        ac[timingKey(prefix, field)] ?? ac[timingKey('ltd', field)];
      return [
        {
          section: title,
          parameter: `First ${title} Start Date`,
          areas: areas.map((ac) =>
            fmtDate(
              moment(refDate)
                .add(value(ac, 'StartOffsetMonths'), 'months')
                .format('YYYY-MM-DD')
            )
          ),
        },
        {
          section: '',
          parameter: 'Permit \u2192 Spud (months)',
          areas: areas.map((ac) => fmt(value(ac, 'PermitToSpudLagMonths'))),
        },
        {
          section: '',
          parameter: 'Spud \u2192 Completion (months)',
          areas: areas.map((ac) => fmt(value(ac, 'SpudToCompletionLagMonths'))),
        },
        {
          section: '',
          parameter: 'Completion \u2192 FP (months)',
          areas: areas.map((ac) =>
            fmt(value(ac, 'CompletionToFirstProductionLagMonths'))
          ),
        },
      ];
    }),
    // Permit section
    {
      section: 'Permit',
      parameter: 'Reference \u2192 Spud (months)',
      areas: areas.map((ac) => fmt(ac.permitReferenceToSpudLagMonths)),
    },
    {
      section: '',
      parameter: 'Spud \u2192 Completion (months)',
      areas: areas.map((ac) => fmt(ac.permitSpudToCompletionLagMonths)),
    },
    {
      section: '',
      parameter: 'Completion \u2192 FP (months)',
      areas: areas.map((ac) =>
        fmt(ac.permitCompletionToFirstProductionLagMonths)
      ),
    },
    // DUC section
    {
      section: 'DUC',
      parameter: 'Reference \u2192 Completion (months)',
      areas: areas.map((ac) => fmt(ac.ducReferenceToCompletionLagMonths)),
    },
    {
      section: '',
      parameter: 'Completion \u2192 FP (months)',
      areas: areas.map((ac) => fmt(ac.ducCompletionToFirstProductionLagMonths)),
    },
  ];

  return rows;
};

const DpsDetailPanel: FC<Props> = ({
  dpsId,
  open,
  onClose,
  evaluationCalculationId,
}) => {
  const dpsHref =
    open && dpsId
      ? `/development_pace_scenarios/${dpsId}${
          evaluationCalculationId
            ? `?evaluation_calculation_id=${evaluationCalculationId}`
            : ''
        }`
      : undefined;

  const dpsResource = useAtomValue(
    useMemo(
      () => createResourceAtom<DevelopmentPaceScenarioResponse>(dpsHref),
      [dpsHref]
    )
  );
  const { loading, data: dps } = formatResource(dpsResource);

  const handleOpenNewTab = useCallback(() => {
    if (dpsId) {
      window.open(
        `/development_pace_scenarios/${dpsId}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  }, [dpsId]);

  const rows = useMemo(() => (dps ? buildRows(dps) : []), [dps]);
  const areas = dps?.areaConfigs ?? [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size={900}
      className={style.drawer}
    >
      <DrawerHeader className={style.header}>
        <div className={style.headerTitle}>
          <span className={style.headerSubtitle}>
            Development Pace Scenario
          </span>
          <span className={style.headerName}>{dps?.name ?? ''}</span>
        </div>
        <div className={style.headerActions}>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Launch}
            iconDescription="Open in new tab"
            hasIconOnly
            onClick={handleOpenNewTab}
          />
          <Button
            kind="ghost"
            size="sm"
            renderIcon={(props) => <Close size={20} {...props} />}
            iconDescription="Close"
            hasIconOnly
            onClick={onClose}
          />
        </div>
      </DrawerHeader>
      <DrawerContent className={style.content}>
        {loading && <p>Loading...</p>}

        {!loading && dps && (
          <>
            <h4 className={style.sectionTitle}>Scenario Details</h4>
            <div className={style.tableWrapper}>
              <table className={style.detailTable}>
                <thead>
                  <tr className={style.detailTableHeadRow}>
                    <th
                      className={`${style.detailTableCell} ${style.detailTableHeader} ${style.sectionCol}`}
                    >
                      Section
                    </th>
                    <th
                      className={`${style.detailTableCell} ${style.detailTableHeader} ${style.paramCol}`}
                    >
                      Parameter
                    </th>
                    {areas.map((ac) => {
                      const name =
                        ac.aliasName ||
                        [ac.configurableCategory, ac.configurableDisplayName]
                          .filter(Boolean)
                          .join(' \u00b7 ') ||
                        `Area ${ac.id}`;
                      return (
                        <th
                          key={ac.id}
                          className={`${style.detailTableCell} ${style.detailTableHeader} ${style.valueCol}`}
                        >
                          {name}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      <td
                        className={`${style.detailTableCell} ${style.sectionCell}`}
                      >
                        {row.section}
                      </td>
                      <td
                        className={`${style.detailTableCell} ${style.paramCell}`}
                      >
                        {row.parameter}
                      </td>
                      {row.areas.map((val, j) => (
                        <td
                          key={j}
                          className={`${style.detailTableCell} ${style.valueCell}`}
                        >
                          {val}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export { DpsDetailPanel };
